import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Collapse from '@mui/material/Collapse';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { makePalette } from 'material-color-tool';
import React, { ReactNode, useEffect, useState } from 'react';
import CollapseExpandButton from './CollapseExpandButton';
import { useExpand } from './ExpandContext';
import PropertiesButton from './PropertiesButton';

const PREFIX = 'MapItemContainer';

const classes = {
  card: `${PREFIX}-card`,
  children: `${PREFIX}-children`,
  icon: `${PREFIX}-icon`,
  header: `${PREFIX}-header`,
  headerContent: `${PREFIX}-headerContent`
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.card}`]: {
    backgroundColor: theme.palette.background.default,
    borderLeftWidth: theme.spacing(1),
    borderLeftStyle: 'solid',
    marginBottom: theme.spacing(1),
    minHeight: '77px',
    color: alpha(theme.palette.grey[900], 0.9)
  },

  [`& .${classes.children}`]: {
    paddingLeft: theme.spacing(1)
  },

  [`& .${classes.icon}`]: {
    visibility: 'hidden'
  },

  [`& .${classes.header}`]: {
    minHeight: '77px',
    '&:hover ': {
      '& .MapItemContainer-icon': {
        visibility: 'visible'
      }
    }
  },

  [`& .${classes.headerContent}`]: {
    minWidth: 0
  }
}));

interface MapItemContainerProps {
  title: string;
  children?: ReactNode;
  subheader?: ReactNode;
  hierarchyLevel?: number;
  onPropertiesClick?: () => void;
}

const MapItemContainer = ({
  title,
  hierarchyLevel = -1,
  subheader,
  children,
  onPropertiesClick
}: MapItemContainerProps) => {
  const theme = useTheme();

  const { isAllExpanded, resetAll } = useExpand();

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (isAllExpanded !== undefined) setIsExpanded(isAllExpanded.state);
  }, [isAllExpanded]);

  const iconOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    resetAll();
    setIsExpanded(!isExpanded);
  };

  const getColor = (level: number) => {
    const shadePalette = makePalette(theme.palette.primary.main);

    switch (level) {
      case 0:
        return shadePalette[8];
      case 1:
        return shadePalette[6];
      case 2:
        return shadePalette[4];
      case 3:
        return shadePalette[2];
      default:
        return shadePalette[0];
    }
  };

  const getButton = () => {
    return (
      <CollapseExpandButton
        onClick={iconOnClick}
        tooltipText={isExpanded ? 'collapse' : 'expand'}
        data-testid="content-icon-click"
      />
    );
  };

  return (
    <StyledCard
      data-testid="map-content"
      className={classes.card}
      raised
      style={
        hierarchyLevel !== -1
          ? { borderColor: getColor(hierarchyLevel) }
          : { borderLeftStyle: 'none' }
      }
    >
      <CardHeader
        className={classes.header}
        data-testid="content-title"
        titleTypographyProps={{ variant: 'body1' }}
        title={title}
        action={
          <>
            <PropertiesButton
              className={classes.icon}
              onClick={
                onPropertiesClick ||
                (() => console.log('properties clicked, no onPropertiesClick given...'))
              }
              data-testid="content-properties-icon-click"
            />
            {children ? getButton() : null}
          </>
        }
        subheader={<div data-testid="content-subheader">{subheader}</div>}
        classes={{
          content: classes.headerContent
        }}
      />
      {children ? (
        <Collapse
          data-testid="expanded-content"
          className={classes.children}
          in={isExpanded}
          timeout={{ enter: 200, exit: 250 }}
          unmountOnExit
        >
          {children || null}
        </Collapse>
      ) : null}
    </StyledCard>
  );
};

export default MapItemContainer;
